/**
 * Serac /pricing page — its own hero over the shared tier ladder.
 *
 * The ladder is flat and bundled: Free, Solo, Pro, Team & Enterprise, rendered
 * by SeracPricingSection from TIER_CARDS (portal/backend/src/utils/modules.ts →
 * public/modules.json). À-la-carte module purchasing is retired
 * (ALACARTE_BILLING_ENABLED = false), so the hero must not sell "pay for the
 * pieces you need" over four fixed bundles — that contradiction is what the
 * copy below was rewritten to remove. The wall between tiers is SCALE
 * (instances, history, seats), plus the consultant toolkit from Pro.
 *
 * Indexing: this page is public marketing on the apex and must stay indexable.
 * The duplicate at dashboard.serac.build/pricing is handled at the HOST level
 * (portal/backend/src/index.ts serves that host `Disallow: /`, 404s its
 * sitemap and sets X-Robots-Tag: noindex) — a `noindex` here cannot tell the
 * two hosts apart and would de-list the canonical URL with them.
 */
import { useTranslation } from 'react-i18next';
import SeracTopNav from '../components/marketing/SeracTopNav';
import SeracMarketingFooter from '../components/marketing/SeracMarketingFooter';
import SeracPricingSection from '../components/marketing/SeracPricingSection';
import { useIsMobile } from '../hooks/useMediaQuery';
import { usePageMeta } from '../hooks/usePageMeta';

export default function Pricing() {
  usePageMeta({
    title: 'Serac pricing — free open-source MCP server, or flat hosted tiers',
    description:
      'Serac pricing. The open-source ServiceNow MCP server is free under Apache License 2.0 and works with the agent you already run; the hosted platform is a flat ladder — Free, Solo at EUR 19 per month for one seat, Pro at EUR 49 per month for up to three seats, Team and Enterprise on contract. Bring your own LLM keys.',
    canonical: 'https://serac.build/pricing',
    ogType: 'website',
  });
  return (
    <div
      style={{
        background: 'var(--bg)',
        color: 'var(--fg)',
        minHeight: '100vh',
        fontFamily: 'var(--font-text)',
      }}
    >
      <SeracTopNav active="pricing" />
      <main>
        <PricingHero />
        <SeracPricingSection bare />
      </main>
      <SeracMarketingFooter />
    </div>
  );
}

function PricingHero() {
  const { t } = useTranslation(['billing', 'common']);
  const isMobile = useIsMobile();
  return (
    <section
      style={{
        position: 'relative',
        overflow: 'hidden',
        padding: isMobile ? '56px 16px 24px' : '80px 32px 32px',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -200,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 900,
          height: 500,
          background:
            'radial-gradient(ellipse at center, oklch(0.5 0.14 205 / 0.25), transparent 65%)',
          filter: 'blur(20px)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          maxWidth: 900,
          margin: '0 auto',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div className="t-eyebrow" style={{ marginBottom: 16 }}>
          {t('billing:pricing.eyebrow')}
        </div>
        <h1
          className="t-display"
          style={{
            fontSize: 'clamp(28px, 5vw, 48px)',
            margin: '0 0 16px',
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
          }}
        >
          {t('billing:pricing.heroTitle')}
        </h1>
        <p
          style={{
            fontSize: isMobile ? 15 : 17,
            color: 'var(--fg-muted)',
            maxWidth: 600,
            margin: '0 auto',
            lineHeight: 1.55,
          }}
        >
          {t('billing:pricing.heroSubtitle')}
        </p>
      </div>
    </section>
  );
}
